import axios from "axios";
import { SERVICE_API_URL } from "../config/serviceConstant";

/**
 * Fetch Shopify customers
 */
export const getShopifyCustomers = async (headers, params = {}) => {
  try {
    const config = {
      method: "GET",
      url: SERVICE_API_URL + "/shopify/customers",
      headers,
      params,
    };
    const resp = await axios(config);
    return resp?.data?.customers || [];
  } catch (error) {
    console.error("getShopifyCustomers error:", error);
    return [];
  }
};

// Backend convention: on failure the response body looks like
//   { data: null, error: "human-readable message" }
// We throw so callers can catch and surface the message, instead of silently
// swallowing it as a null payload.
const unwrapOrThrow = (resp, ...pickers) => {
  const body = resp?.data;
  if (body && typeof body === "object" && body.error && !body.data) {
    throw new Error(body.error);
  }
  for (const pick of pickers) {
    const val = pick(body);
    if (val !== undefined && val !== null) return val;
  }
  return null;
};

/**
 * Fetch a single Shopify customer by id
 */
export const getShopifyCustomerById = async (headers, id) => {
  try {
    const resp = await axios({
      method: "GET",
      url: `${SERVICE_API_URL}/shopify/customers/${id}`,
      headers,
    });
    // Backend returns a shaped bundle: { data: { profile, metrics, orders, abandoned_carts, shopDomain } }
    return unwrapOrThrow(
      resp,
      (b) => b?.data,
      (b) => b?.customer,
      (b) => b?.customers?.[0]
    );
  } catch (error) {
    console.error("getShopifyCustomerById error:", error);
    // Preserve backend error message when present; otherwise rethrow the raw error
    const msg = error?.response?.data?.error || error?.message;
    throw new Error(msg || "Failed to fetch Shopify customer");
  }
};

/**
 * Fetch Shopify products
 */
export const getShopifyProducts = async (headers, params = {}) => {
  try {
    const config = {
      method: "GET",
      url: SERVICE_API_URL + "/shopify/products",
      headers,
      params,
    };
    const resp = await axios(config);
    return resp?.data?.products || [];
  } catch (error) {
    console.error("getShopifyProducts error:", error);
    return [];
  }
};

/**
 * Fetch a single Shopify product by id
 */
export const getShopifyProductById = async (headers, id) => {
  try {
    const resp = await axios({
      method: "GET",
      url: `${SERVICE_API_URL}/shopify/products/${id}`,
      headers,
    });
    return unwrapOrThrow(
      resp,
      (b) => b?.data,
      (b) => b?.product,
      (b) => b?.products?.[0]
    );
  } catch (error) {
    console.error("getShopifyProductById error:", error);
    const msg = error?.response?.data?.error || error?.message;
    throw new Error(msg || "Failed to fetch Shopify product");
  }
};

/**
 * Fetch Shopify orders
 */
export const getShopifyOrders = async (headers, params = {}) => {
  try {
    const config = {
      method: "GET",
      url: SERVICE_API_URL + "/shopify/orders",
      headers,
      params,
    };
    const resp = await axios(config);
    return resp?.data?.orders || [];
  } catch (error) {
    console.error("getShopifyOrders error:", error);
    return [];
  }
};

/**
 * Fetch a single Shopify order by id
 */
export const getShopifyOrderById = async (headers, id) => {
  try {
    const resp = await axios({
      method: "GET",
      url: `${SERVICE_API_URL}/shopify/orders/${id}`,
      headers,
    });
    return unwrapOrThrow(
      resp,
      (b) => b?.data,
      (b) => b?.order,
      (b) => b?.orders?.[0]
    );
  } catch (error) {
    console.error("getShopifyOrderById error:", error);
    const msg = error?.response?.data?.error || error?.message;
    throw new Error(msg || "Failed to fetch Shopify order");
  }
};

/**
 * Fetch Shopify coupons / price rules
 */
export const getShopifyCoupons = async (headers, params = {}) => {
  try {
    const config = {
      method: "GET",
      url: SERVICE_API_URL + "/shopify/coupons",
      headers,
      params,
    };
    const resp = await axios(config);
    return resp?.data?.coupons || resp?.data?.price_rules || [];
  } catch (error) {
    console.error("getShopifyCoupons error:", error);
    return [];
  }
};

/**
 * Create a Shopify webhook subscription
 */
export const createShopifyWebhook = async (headers, body = {}) => {
  try {
    const resp = await axios({
      method: "POST",
      url: SERVICE_API_URL + "/shopify/webhooks",
      headers,
      data: body,
    });
    return resp?.data || null;
  } catch (error) {
    console.error("createShopifyWebhook error:", error);
    return null;
  }
};

/**
 * Fetch all Shopify webhooks
 */
export const getShopifyWebhooks = async (headers) => {
  try {
    const resp = await axios({
      method: "GET",
      url: SERVICE_API_URL + "/shopify/webhooks",
      headers,
    });
    // ALWAYS return an array. The backend sometimes responds with a non-array
    // object (e.g. { success: true } when a store has no webhooks yet); callers
    // run `.filter()` on the result, so a non-array would throw and abort the
    // whole default-setup flow.
    const data = resp?.data;
    const list = data?.webhooks ?? data?.data ?? data;
    return Array.isArray(list) ? list : [];
  } catch (error) {
    console.error("getShopifyWebhooks error:", error);
    return [];
  }
};

/**
 * Delete every Shopify webhook for the store identified by the headers
 * (orgid + connname + shopifyshopurl). Single backend call — the server
 * iterates internally, so the UI doesn't have to.
 */
export const deleteAllShopifyWebhooks = async (headers) => {
  try {
    const resp = await axios({
      method: "DELETE",
      url: SERVICE_API_URL + "/shopify/webhooks",
      headers,
    });
    return resp?.data || true;
  } catch (error) {
    console.error("deleteAllShopifyWebhooks error:", error);
    return null;
  }
};

/**
 * Delete a Shopify webhook by ID
 */
export const deleteShopifyWebhook = async (headers, webhookId, settingId) => {
  try {
    const resp = await axios({
      method: "DELETE",
      url: SERVICE_API_URL + "/shopify/webhooks/" + webhookId,
      headers,
      params: { setting_id: settingId },
    });
    return resp?.data || true;
  } catch (error) {
    console.error("deleteShopifyWebhook error:", error);
    return null;
  }
};

/**
 * Fetch Shopify resource fields for a given trigger event
 */
export const getShopifyFields = async (headers, params = {}) => {
  try {
    const config = {
      method: "GET",
      url: SERVICE_API_URL + "/shopify/fields",
      headers,
      params,
    };
    const resp = await axios(config);
    return resp?.data?.fields || resp?.data || [];
  } catch (error) {
    console.error("getShopifyFields error:", error);
    return [];
  }
};

// ── Logs ──

/**
 * Fetch sync logs with optional filters
 */
export const getLogs = async (headers, params = {}) => {
  try {
    const resp = await axios({
      method: "GET",
      url: SERVICE_API_URL + "/logs",
      headers,
      params,
    });
    const d = resp?.data;
    const logs = d?.logs || d?.data || d?.records || [];
    const total = d?.total ?? d?.count ?? logs.length;
    return { logs, total };
  } catch (error) {
    console.error("getLogs error:", error);
    return { logs: [], total: 0 };
  }
};

/**
 * Export logs as CSV
 */
export const exportLogs = async (headers, params = {}) => {
  try {
    const resp = await axios({
      method: "GET",
      url: SERVICE_API_URL + "/logs/export",
      headers,
      params,
      responseType: "blob",
    });
    return resp?.data || null;
  } catch (error) {
    console.error("exportLogs error:", error);
    return null;
  }
};

/**
 * Fetch Zoho CRM module field metadata via the backend's shopify/getcrmfieids
 * proxy endpoint. Used by initZohoCustomAction so the wizard's Zoho field
 * pickers can render without a direct ZOHO.CRM.META.getFields round-trip.
 *
 * Body shape (per backend contract):
 *   {
 *     entity: <Module API Name>,
 *     displayName: true,
 *     apiName: true,
 *     returnValues: ["fields"],
 *     fetchAdditionalFieldTypes: ["entity","lookup","ownerlookup","subform"]
 *   }
 *
 * Response is normalised to the same `[{ api_name, display_label, data_type,
 * lookup, subform, system_mandatory, ... }]` shape that ZOHO.CRM.META.getFields
 * returns, so existing consumers don't need to change.
 */
// Light-touch normalisation: backend's camelCase keys (apiName, displayName,
// type, lookupModuleApiName, required, …) → snake_case parity with what
// ZOHO.CRM.META.getFields returns. Original keys are preserved via spread so
// downstream code that reads either shape keeps working.
const normaliseShopifyCrmField = (f) => {
  const apiName = f.api_name || f.apiName || f.api || "";
  const displayLabel =
    f.display_label || f.displayName || f.displayLabel || f.label || apiName;
  const dataType = f.data_type || f.dataType || f.type || "";
  const lookupModuleApiName =
    f.lookupModuleApiName ||
    f.lookup?.module?.api_name ||
    (typeof f.lookup?.module === "string" ? f.lookup.module : "") ||
    "";
  return {
    ...f,
    api_name: apiName,
    display_label: displayLabel,
    field_label: f.field_label || displayLabel,
    data_type: dataType,
    lookup: lookupModuleApiName
      ? { module: { api_name: lookupModuleApiName } }
      : f.lookup,
    system_mandatory:
      f.system_mandatory === true ||
      f.systemMandatory === true ||
      f.required === true,
    field_read_only: f.fieldReadOnly === true || f.field_read_only === true,
  };
};

// Convert a group key like "Account_Name" to a human label using the first
// field's lookupModuleDisplayName when present, otherwise underscores → spaces.
const prettifyCrmGroupName = (key, list) => {
  const fromField = list?.[0]?.lookupModuleDisplayName;
  if (fromField) return fromField;
  return String(key).replace(/_/g, " ");
};

export const getCrmFieldsViaShopify = async (headers, entity) => {
  try {
    const resp = await axios({
      method: "POST",
      url: SERVICE_API_URL + "/shopify/getcrmfieids",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      data: {
        entity,
        displayName: true,
        apiName: true,
        returnValues: ["fields"],
        fetchAdditionalFieldTypes: ["entity", "lookup", "ownerlookup", "subform"],
      },
    });
    const body = resp?.data;
    const out = [];

    // New shape — { fieldNames: { <groupApiName>: [field, …], … }, … }.
    // The main module's own fields appear under a key matching `entity`;
    // additional groups (Owner, Account_Name, Line_Items, …) carry the
    // cross-module fields reachable from that path. We flatten everything
    // into a single list, sort by group (main module first, then alpha),
    // and tag each field with `_group` so the UI can render an Autocomplete
    // grouped by module display name.
    const fieldNames = body?.fieldNames;
    if (
      fieldNames &&
      typeof fieldNames === "object" &&
      !Array.isArray(fieldNames)
    ) {
      // Build group entries in deterministic order: main module first, the
      // rest alphabetised by their human label.
      const groupEntries = Object.entries(fieldNames)
        .filter(([, list]) => Array.isArray(list))
        .sort(([aKey, aList], [bKey, bList]) => {
          if (aKey === entity) return -1;
          if (bKey === entity) return 1;
          return prettifyCrmGroupName(aKey, aList).localeCompare(
            prettifyCrmGroupName(bKey, bList)
          );
        });

      for (const [groupKey, list] of groupEntries) {
        const isMain = groupKey === entity;
        const groupLabel = prettifyCrmGroupName(groupKey, list);
        for (const raw of list) {
          const norm = normaliseShopifyCrmField(raw);
          // Qualify cross-module field api_names with the group prefix so the
          // backend can resolve the path on save (e.g. "Account_Name.Industry").
          if (!isMain && norm.api_name) {
            norm.api_name = `${groupKey}.${norm.api_name}`;
          }
          norm._group = groupLabel;
          norm._groupKey = groupKey;
          norm._isMainModule = isMain;
          out.push(norm);
        }
      }
      return out.filter((f) => !f.field_read_only);
    }

    // Legacy / fallback: flat fields array under various envelope keys.
    const flat =
      body?.data?.fields ||
      body?.fields ||
      body?.data ||
      (Array.isArray(body) ? body : []) ||
      [];
    if (!Array.isArray(flat)) return [];
    for (const raw of flat) {
      const norm = normaliseShopifyCrmField(raw);
      norm._group = entity;
      norm._groupKey = entity;
      norm._isMainModule = true;
      out.push(norm);
    }
    return out.filter((f) => !f.field_read_only);
  } catch (error) {
    console.error("getCrmFieldsViaShopify error:", error);
    return [];
  }
};
